import { makeAutoObservable, toJS } from 'mobx'
import { Attachment } from 'entities/Attachment/model/Attachment'
import { RequestAttachment } from 'entities/Attachment/model/RequestAttachment'
import {
  AgentActionType,
  type IAgentActionAddTagSetting,
  type IAgentActionContactNoteSetting,
  type IAgentActionNotePayload,
  type IAgentActionRemoveTagSetting,
  type IAgentActionSendMessageSetting,
  type IAgentActionSettings,
  type IAgentActionTagPayload,
  type IAgentActionType,
  type IAgentMessagePayload,
} from '../api/types'

export type IAgentActionsFactoryOptions = {
  sendMessageAutoMode?: boolean
}

export class AgentActionTag {
  static Create(type: typeof AgentActionType.AddTag | typeof AgentActionType.RemoveTag) {
    return new AgentActionTag({ type, settings: { tag_ids: [] } })
  }

  type!: typeof AgentActionType.AddTag | typeof AgentActionType.RemoveTag
  tagIds: number[] = []

  constructor(item: IAgentActionAddTagSetting | IAgentActionRemoveTagSetting) {
    this.type = item.type
    this.syncOrigin(item.settings)
    makeAutoObservable(this)
  }

  syncIds(ids: number[]) {
    this.tagIds = ids
  }

  syncOrigin(item: IAgentActionTagPayload) {
    this.tagIds = item.tag_ids
  }

  toJSON(): IAgentActionAddTagSetting | IAgentActionRemoveTagSetting {
    return toJS({
      type: this.type,
      settings: { tag_ids: this.tagIds },
    })
  }
}

export class AgentActionNote {
  static Create() {
    return new AgentActionNote({
      type: AgentActionType.ContactNote,
      settings: { note: '' },
    })
  }

  type = AgentActionType.ContactNote
  note = ''

  constructor(item: IAgentActionContactNoteSetting) {
    this.syncOrigin(item.settings)
    makeAutoObservable(this)
  }

  sync(note: string) {
    this.note = note
  }

  syncOrigin(item: IAgentActionNotePayload) {
    this.note = item?.note ?? ''
  }

  toJSON(): IAgentActionContactNoteSetting {
    return toJS({
      type: AgentActionType.ContactNote,
      settings: { note: this.note },
    })
  }
}

export class AgentActionSendMessage {
  static Create(opts: { withAutoMode?: boolean } = {}) {
    return new AgentActionSendMessage(
      {
        type: AgentActionType.SendMessage,
        settings: { message: '', media_url: [] },
      },
      opts
    )
  }

  type = AgentActionType.SendMessage
  message = ''
  attachments: Attachment[] = []
  private readonly _withAutoMode: boolean
  private _isAutoMessageType = false

  constructor(item: IAgentActionSendMessageSetting, opts: { withAutoMode?: boolean } = {}) {
    this._withAutoMode = opts.withAutoMode ?? false
    this.syncOrigin(item.settings)
    makeAutoObservable(this)
  }

  get withAutoMode() {
    return this._withAutoMode
  }

  get isAutoMessageType() {
    return this._isAutoMessageType
  }

  setIsAutoMessageType = (value: boolean) => {
    this._isAutoMessageType = value
  }

  sync(message: string, attachments: Attachment[]) {
    this.message = message
    this.attachments = attachments
  }

  syncOrigin(item: IAgentMessagePayload) {
    this.message = item?.message ?? ''
    this.attachments =
      item?.media_url?.map((requestAttachment) => new Attachment({ requestAttachment })) ?? []
    this._isAutoMessageType = item?.type === 'auto'
  }

  toJSON(): IAgentActionSendMessageSetting {
    const settings: IAgentMessagePayload = {
      message: this.message,
      media_url: this.attachments.map((attachment) => new RequestAttachment(attachment)),
    }
    if (this._withAutoMode) settings.type = this._isAutoMessageType ? 'auto' : 'manual'
    return toJS({
      type: AgentActionType.SendMessage,
      settings,
    })
  }
}

export type IAgentActionModel = AgentActionTag | AgentActionNote | AgentActionSendMessage

export class AgentActions {
  static buildAction(type: IAgentActionType, opts: IAgentActionsFactoryOptions): IAgentActionModel {
    switch (type) {
      case AgentActionType.AddTag:
      case AgentActionType.RemoveTag:
        return AgentActionTag.Create(type)
      case AgentActionType.ContactNote:
        return AgentActionNote.Create()
      case AgentActionType.SendMessage:
        return AgentActionSendMessage.Create({ withAutoMode: opts.sendMessageAutoMode })
      default:
        throw new TypeError(`Unknown agent action type: "${type}"`)
    }
  }

  actions: IAgentActionModel[] = []
  private readonly _options: IAgentActionsFactoryOptions

  constructor(items?: IAgentActionSettings[] | null, options: IAgentActionsFactoryOptions = {}) {
    this._options = options
    if (items) this.syncOrigin(items)
    makeAutoObservable(this)
  }

  addAction(type: IAgentActionType) {
    const action = AgentActions.buildAction(type, this._options)
    this.actions = [...this.actions, action]
    return action
  }

  removeAction(index: number) {
    const next = [...this.actions]
    const [removed] = next.splice(index, 1)
    this.actions = next
    return removed
  }

  syncOrigin(items?: IAgentActionSettings[] | null) {
    this.actions =
      items
        ?.map((item): IAgentActionModel | null => {
          switch (item.type) {
            case AgentActionType.AddTag:
            case AgentActionType.RemoveTag:
              return new AgentActionTag(item)
            case AgentActionType.ContactNote:
              return new AgentActionNote(item)
            case AgentActionType.SendMessage:
              return new AgentActionSendMessage(item, {
                withAutoMode: this._options.sendMessageAutoMode,
              })
            default:
              return null
          }
        })
        .filter((item): item is IAgentActionModel => item !== null) ?? []
  }

  toJSON(): IAgentActionSettings[] {
    return toJS(this.actions.map((action) => action.toJSON()))
  }

  reset() {
    this.actions = []
  }
}
